import { getAppConfiguration } from "appConfig";
import { AxiosResponse } from "axios";
import {
  type BackgroundTemplate,
  type editReservationDetailsType,
  type UpdateReservationResponseType,
} from "utils";
import { axiosEjFetch as axiosFetch } from "./axiosEjHelpers";

const endpoint = "/api/reservation";

export type ReservationListFilters = {
  eventStartFrom?: string | null;
  eventStartTo?: string | null;
  reservationCreatedFrom?: string | null;
  reservationCreatedTo?: string | null;
  latestMediaWithinDays?: number | null;
};

export async function getReservations(
  includeMediaInfo = false,
  filters?: ReservationListFilters
) {
  const params = new URLSearchParams();
  params.set("includeMediaInfo", String(includeMediaInfo));
  if (filters?.eventStartFrom) params.set("eventStartFrom", filters.eventStartFrom);
  if (filters?.eventStartTo) params.set("eventStartTo", filters.eventStartTo);
  if (filters?.reservationCreatedFrom)
    params.set("reservationCreatedFrom", filters.reservationCreatedFrom);
  if (filters?.reservationCreatedTo)
    params.set("reservationCreatedTo", filters.reservationCreatedTo);
  if (filters?.latestMediaWithinDays != null)
    params.set("latestMediaWithinDays", String(filters.latestMediaWithinDays));
  return axiosFetch.get(`${endpoint}?${params.toString()}`);
}

export async function getMyReservations(includeMediaInfo = false) {
  return axiosFetch.get(
    `${endpoint}/myReservation?includeMediaInfo=${includeMediaInfo}`
  );
}

export async function getReservationById(
  reservationId: string,
  includeMediaInfo = false
): Promise<editReservationDetailsType> {
  const query = includeMediaInfo ? `?includeMediaInfo=true` : "";
  return axiosFetch.safeGet(`${endpoint}/${reservationId}${query}`);
}

export async function getMyReservationById(
  reservationId: string,
  includeMediaInfo = false
): Promise<editReservationDetailsType> {
  const query = includeMediaInfo ? `?includeMediaInfo=true` : "";
  try {
    return await axiosFetch.safeGet(`${endpoint}/myReservation/${reservationId}${query}`);
  } catch {
    return await axiosFetch.safeGet(`${endpoint}/${reservationId}${query}`);
  }
}

export async function postReservation(
  body: any
): Promise<AxiosResponse<UpdateReservationResponseType>> {
  if (body.id) {
    body.couponCode = null; // coupon code can only be applied when creating a reservation
  }
  const appConfiguration = await getAppConfiguration();
  const updatedBody = {
    ...body,
    providerId: body.providerId || appConfiguration.providerId,
  };
  return axiosFetch.post(`${endpoint}/`, updatedBody);
}

export async function deleteReservation(reservationId: string) {
  return axiosFetch.del(`${endpoint}/${reservationId}`);
}

export async function setDeviceShipped(
  reservationId: string,
  isShipped: boolean
) {
  const body = [
    {
      op: "replace",
      path: "/isDeviceShipped",
      value: isShipped,
    },
  ];
  return axiosFetch.patch(`${endpoint}/${reservationId}`, body);
}

export async function reservationEnded<T>(body: T) {
  return axiosFetch.post(`${endpoint}/reservationEnded`, body);
}

export async function assignBackgroundTemplate(
  reservationId: string,
  backgroundTemplateId: string
) {
  return axiosFetch.post<any, any, any>(
    `${endpoint}/${reservationId}/backgrounds/${backgroundTemplateId}`
  );
}

export async function unAssignBackgroundTemplate(
  reservationId: string,
  backgroundTemplateId: string
) {
  return axiosFetch.del(
    `${endpoint}/${reservationId}/backgrounds/${backgroundTemplateId}`
  );
}

export async function setBackgroundTemplateActivated(
  reservationId: string,
  backgroundTemplateId: string,
  active: boolean
) {
  const body = [
    {
      op: "replace",
      path: `/isActive`,
      value: active,
    },
  ];
  return axiosFetch.patch(
    `${endpoint}/${reservationId}/backgrounds/${backgroundTemplateId}`,
    body
  );
}

export async function setBackgroundTemplateAsDefault(
  reservationId: string,
  backgroundTemplateId: string
): Promise<BackgroundTemplate> {
  const response = await axiosFetch.safePost<any, any, any, BackgroundTemplate>(
    `${endpoint}/${reservationId}/setDefaultBackground/${backgroundTemplateId}`
  );
  return response.data;
}

export async function setReservationGratis(reservationId: string) {
  return axiosFetch.post(`${endpoint}/${reservationId}/setGratis`, {});
}

export async function getReservationMediaInfo(reservationId: string) {
  return axiosFetch.get(`${endpoint}/${reservationId}/mediaInfo`);
}
